<template>
    <component
        :is="mainComponent"
        :class="{
            'tooltip': true,
            'tooltip_no-margin': noMargin,
            'tooltip_inline': !isBlock,
        }"
        v-bind="componentProps"
        v-on="componentEvents"
    >
        <template #activator>
            <div
                ref="activator"
                class="_pointer"
                tabindex="0"
                @click="onClick"
                @focus="isFocusedActivator = true"
                @blur="isFocusedActivator = false"
                @mouseover="onHover"
                @mouseout="onHover"
            >
                <slot name="activator">
                    <div
                        class="ico"
                        style="mask-size: contain;"
                        :class="{
                            'ico_color_gray-dark': color === 'gray',
                            'ico_color_white': color === 'white',
                            'ico_size_16': size === 'md',
                            'ico_size_24': size === 'lg',
                            'ico-info-bold': icon === 'info',
                            'ico-question-bold': icon === 'question',
                            'ico-alert-bold': icon === 'alert',
                        }"
                    />
                </slot>
            </div>
        </template>
        <template v-if="mainComponent === UiPopup" #arrow>
            <div class="tooltip__arrow"/>
        </template>
        <component
            :is="contentComponent"
            v-show="localValue"
            v-model="localValue"
        >
            <div
                ref="content"
                :class="{
                    'tooltip__content': contentComponent === 'div',
                    'tooltip__sheet-content': contentComponent === UiBottomSheet,
                }"
                tabindex="-1"
                @focus="isFocusedContent = true"
                @blur="isFocusedContent = false"
            >
                <div
                    v-if="text"
                    :class="{
                        'tooltip__inner': !isMobile,
                    }"
                    v-html="text"
                />
                <div
                    v-if="$slots.default"
                    :class="{
                        'tooltip__inner': !isMobile,
                    }"
                >
                    <slot />
                </div>
                <div
                    v-if="closable && !isMobile"
                    class="tooltip__inline-append"
                >
                    <button
                        class="ico ico-cross-bold ico_color_white ico_size_16 _ml-8 _pointer"
                        type="button"
                        @click="toggleValue(false)"
                    />
                </div>
            </div>
        </component>
    </component>
</template>

<script>
import { computed, onMounted, onUnmounted, reactive, ref, watch, } from 'vue';
import UiPopup from '../components-manual/ui/UiPopup.vue';
import { useClickOutside } from '../assets/js/vue/utlis/useClickOutside';
import debounce from 'lodash/debounce';
import UiBottomSheet from '../components-manual/ui/UiBottomSheet.vue';
import { useStore } from '../store';

    const TooltipOnlyActivator = {
        name: 'TooltipOnlyActivator',
        render(h) {
            return h(
                'div',
                [this.$slots.activator, this.$slots.default],
            );
        },
    };

	export default {
		name: 'Tooltip',
        components: {
            UiPopup,
            UiBottomSheet,
            TooltipOnlyActivator,
        },
		props: {
            /**
             * @deprecated вместо него - использовать слот default
             */
			text: {
				type: String,
			},
            /**
             * по усполчанию есть левый отступ у активатора, можно выключить
             */
			noMargin: {
				type: Boolean,
				default: false
			},
            /**
             * тип иконки - активатора
             */
            icon: {
				type: String,
                default: 'question',
                validate: (val) => !val && ['info', 'question', 'alert'].includes(val),
            },
            /**
             * цвет иконки - активатора
             */
            color: {
                type: String,
                default: 'gray',
                validate: (val) => !val && ['info', 'white', 'gray'].includes(val),
            },
            /**
             * размер иконки - активатора
             */
            size: {
				type: String,
                default: 'md',
                validate: (val) => !val && ['md', 'lg'].includes(val),
            },
            /**
             * показывать/скрывать по наведению
             */
            hoverDriven: {
                type: Boolean,
                default: true,
            },
            /**
             * показывать/скрывать по клику
             * если отключен, то в моб версии тултип не будет показан, т.к. там активируется только по клику
             */
            clickDriven: {
                type: Boolean,
                default: true,
            },
            /**
             * показывать/скрывать по фокусу
             */
            focusDriven: {
                type: Boolean,
                default: true,
            },
            /**
             * закрыть по клику вне компонента, можно отключить
             */
            closeOnClickOutside: {
                type: Boolean,
                default: true,
            },
            /**
             * к какому краю активатора выравнивается попап
             */
            align: {
                type: String,
                default: 'end',
                validator: (val) => ['start', 'end', 'center'].includes(val),
            },
            /**
             * с какой стороны активатора расположен попап
             * при выборе top или bottom - он перемещается на противаположную, если не влазит в экран
             */
            placement: {
                type: String,
                default: 'top',
                validator: (val) => ['top', 'bottom', 'left', 'right'].includes(val),
            },
            value: Boolean,
            closable: Boolean,
            isBlock: Boolean,
		},
        setup(props, { emit }) {
            const localValue = ref(false);
            const isHoveredActivator = ref(false);
            const isHoveredContent = ref(false);
            const isFocusedContent = ref(false);
            const isFocusedActivator = ref(false);
            const activator = ref(null);
            const content = ref(null);

            const store = useStore();

            const isMobile = computed(() => store.state.app.isMobile);

            watch(() => props.value, (val) => {
                localValue.value = val;
            }, { immediate: true });

            watch(localValue, (val) => {
                if (val === props.value) return;

                emit('input', val);
            });

            const isHovered = computed(() => isHoveredActivator.value || isHoveredContent.value);

            const toggleValue = debounce((val) => {
                localValue.value = val;
            }, 50);

            const onClickOutside = () => {
                if (!props.closeOnClickOutside) return;
                toggleValue(false);
            };

            const onClick = () => {
                if (!props.clickDriven) return;
                toggleValue(!localValue.value);
            };

            watch(isHovered, (val) => {
                if (isMobile.value || !props.hoverDriven) return;
                toggleValue(val);
            });

            const isFocused = computed(() => isFocusedActivator.value || isFocusedContent.value);

            watch(isFocused, (val) => {
                if (isMobile.value || !props.focusDriven) return;
                toggleValue(val);
            });

            useClickOutside([activator, content], onClickOutside);

            /**
             * @param {PointerEvent} e
             */
            const onHover = debounce((e) => {
                const isEnter = e.type === 'mouseover';

                if (activator.value === e.target || activator.value?.contains(e.target)) {
                    isHoveredActivator.value = isEnter;
                }
            }, 100);

            const alignOffset = computed(() => {
                let offset = 0;

                if (props.align === 'start' || props.align === 'end') {
                    offset = ['top', 'bottom'].includes(props.placement) ? -12 : -10;
                }
                return offset;
            });

            const mainComponent = computed(() => {
                return isMobile.value ? TooltipOnlyActivator : UiPopup;
            });

            const contentComponent = computed(() => {
                return isMobile.value && props.clickDriven ? UiBottomSheet : 'div';
            });

            const popupProps = reactive({
                value: localValue,
                placement: props.placement,
                align: props.align,
                alignOffset: alignOffset,
            });

            const popupEvents = reactive({
                input: (val) => localValue.value = val,
                'update:is-hovered': (val) => isHoveredContent.value = val,
            });

            const componentProps = computed(() => {
                let props = {};
                switch (mainComponent.value) {
                    case UiPopup:
                        props = popupProps;
                        break;
                    default:
                        break;
                }
                return props;
            });

            const componentEvents = computed(() => {
                let events = {};
                switch (mainComponent.value) {
                    case UiPopup:
                        events = popupEvents;
                        break;
                    default:
                        break;
                }
                return events;
            });

            const visibilityChangeHandler = () => {
                if (document.hidden && document.activeElement === activator.value) {
                    activator.value.blur();
                }
            };

            onMounted(() => {
                document.addEventListener('visibilitychange', visibilityChangeHandler);
            });

            onUnmounted(() => {
                document.removeEventListener('visibilitychange', visibilityChangeHandler);
            });

            return {
                localValue,
                isHovered,
                isFocused,
                isFocusedActivator,
                isFocusedContent,
                onClick,
                activator,
                content,
                onHover,
                isHoveredContent,
                alignOffset,
                isMobile,
                mainComponent,
                componentProps,
                componentEvents,
                toggleValue,
                UiPopup,
                TooltipOnlyActivator,
                contentComponent,
                UiBottomSheet,
            };
        },
	}
</script>
<style lang="scss">
@import '../assets/scss/base/variables';
@import '../assets/scss/base/functions';
@import '../assets/scss/base/mixins';

.tooltip {
    @include fluid('margin-left', $ind1, 12px);

    &_inline {
        display: inline-block;
        vertical-align: middle;
    }

    &_no-margin {
        margin-left: 0px;
    }

    &__content {
        position: relative;
        display: flex;
        white-space: normal;
        text-wrap: initial;

        box-sizing: border-box;
        background-color: $grey-517;
        color: $white-101;
        @include fluid('border-radius', $ind1);
        @include fluid('padding-left', 16px);
        @include fluid('padding-right', 16px);
        @include fluid('padding-top', 12px);
        @include fluid('padding-bottom', 12px);
        @include fluid('max-width', 256px);
        @include fluid('font-size', 12px);
        @include fluid('line-height', 16px);
    }

    &__sheet-content {
        white-space: normal;
        text-wrap: initial;
        @include fluid('padding-left', 16px);
        @include fluid('padding-right', 16px);
        @include fluid('padding-top', 12px);
        @include fluid('padding-bottom', 12px);
    }

    &__inner {
    }

    &__arrow {
        @include fluid('width', 20px);
        @include fluid('height', 8px);
        background: url("/local/templates/redesign/src/assets/img/icons/icon-tooltip-arrow-dark-new.svg") no-repeat center / cover;
        z-index: 1;
    }
}

</style>
